import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress,
  Alert,
  Stack,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { parseFile, runImport } from '../import';
import { SOURCE_LABEL } from '../constants';

/**
 * CSV import panel: pick one or more platform exports, see the detected
 * platform + parsed order count, then import (upsert) into Firestore.
 */
export default function ImportPanel({ onImported }) {
  const [files, setFiles] = useState([]); // [{ file, source, orders, error }]
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [summary, setSummary] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Preview + queue a list of files (shared by the file picker and drag-and-drop).
  async function addFiles(picked) {
    const csvs = picked.filter(
      (f) => f.type === 'text/csv' || f.type === 'application/vnd.ms-excel' || /\.csv$/i.test(f.name)
    );
    if (csvs.length === 0) return;
    setSummary(null);
    const previews = await Promise.all(
      csvs.map(async (file) => {
        try {
          const { source, orders, error } = await parseFile(file);
          return { file, source, orders, error };
        } catch (err) {
          return { file, source: null, orders: [], error: err.message };
        }
      })
    );
    setFiles((prev) => [...prev, ...previews]);
  }

  function onPick(e) {
    addFiles(Array.from(e.target.files || []));
    e.target.value = ''; // allow re-picking the same file
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (importing) return;
    addFiles(Array.from(e.dataTransfer.files || []));
  }

  async function onImport() {
    setImporting(true);
    setSummary(null);
    setProgress({ done: 0, total: 0 });
    try {
      const result = await runImport(
        files.map((f) => f.file),
        { onProgress: setProgress }
      );
      setSummary(result);
      // Clean import: clear the queue ready for another. Otherwise keep the files
      // (and the summary) on screen so issues / partial results can be reviewed.
      if (result.errors.length === 0) setFiles([]);
      if (onImported) onImported();
    } catch (err) {
      setSummary({ created: 0, updated: 0, skipped: 0, errors: [err.message], exceptions: [], total: 0 });
    } finally {
      setImporting(false);
    }
  }

  const totalOrders = files.reduce((n, f) => n + (f.orders?.length || 0), 0);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Import orders from CSV
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload the platform exports (Etsy, eBay, Shopify). Re-importing the same file is safe —
          orders are matched by their composite key and merged, never duplicated.
        </Typography>

        <Box
          onDragOver={(e) => {
            e.preventDefault();
            if (!importing) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          sx={{
            mb: 2,
            p: 3,
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            borderRadius: 1,
            bgcolor: dragOver ? 'action.hover' : 'transparent',
            textAlign: 'center',
            transition: 'background-color 120ms, border-color 120ms',
          }}
        >
          <UploadFileIcon color="action" sx={{ fontSize: 36, mb: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Drag &amp; drop CSV files here, or
          </Typography>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={importing}>
            Choose CSV files
            <input hidden type="file" accept=".csv,text/csv" multiple onChange={onPick} />
          </Button>
        </Box>

        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={onImport}
            disabled={importing || files.length === 0 || totalOrders === 0}
          >
            Import {totalOrders > 0 ? `${totalOrders} order${totalOrders === 1 ? '' : 's'}` : ''}
          </Button>
        </Stack>

        {files.length > 0 && (
          <List dense>
            {files.map((f, i) => (
              <ListItem
                key={`${f.file.name}-${i}`}
                secondaryAction={
                  f.source ? (
                    <Chip size="small" color="primary" label={SOURCE_LABEL[f.source]} />
                  ) : (
                    <Chip size="small" color="error" label="Unknown" />
                  )
                }
              >
                <ListItemText
                  primary={f.file.name}
                  secondary={
                    f.error ? f.error : `${f.orders.length} order${f.orders.length === 1 ? '' : 's'}`
                  }
                />
              </ListItem>
            ))}
          </List>
        )}

        {importing && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant={progress.total ? 'determinate' : 'indeterminate'}
              value={progress.total ? (progress.done / progress.total) * 100 : undefined}
            />
            <Typography variant="caption" color="text.secondary">
              Importing {progress.done} / {progress.total}…
            </Typography>
          </Box>
        )}

        {summary && (
          <Box sx={{ mt: 2 }}>
            {(() => {
              const ok = summary.created + summary.updated;
              const failed = summary.errors.length > 0;
              const severity = !failed ? 'success' : ok > 0 ? 'warning' : 'error';
              const headline = !failed
                ? `Import complete — ${ok} order${ok === 1 ? '' : 's'} imported.`
                : ok > 0
                ? `Partial import — ${ok} imported, ${summary.errors.length} issue${summary.errors.length === 1 ? '' : 's'}.`
                : `Import failed — ${summary.errors.length} issue${summary.errors.length === 1 ? '' : 's'}, nothing imported.`;
              return (
                <Alert severity={severity}>
                  {headline}
                  <br />
                  {summary.created} new, {summary.updated} updated
                  {summary.skipped ? `, ${summary.skipped} skipped` : ''}.
                </Alert>
              );
            })()}
            {summary.errors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" color="error">Issues:</Typography>
                {summary.errors.slice(0, 25).map((err, i) => (
                  <Typography key={i} variant="caption" color="error" display="block">
                    {err}
                  </Typography>
                ))}
                {summary.errors.length > 25 && (
                  <Typography variant="caption" color="error" display="block">
                    …and {summary.errors.length - 25} more.
                  </Typography>
                )}
              </Box>
            )}
            {summary.exceptions?.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2">
                  Kept your edits ({summary.exceptions.length}) — platform values differed:
                </Typography>
                {summary.exceptions.slice(0, 20).map((ex, i) => (
                  <Typography key={i} variant="caption" color="text.secondary" display="block">
                    {ex}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
